/**
 * <s-gallery-dots> — mobile-only position indicator for the PDP gallery.
 *
 * Reads the sibling [data-product-gallery] scroller inside the closest [data-gallery-root]
 * (collection product cards) or
 * s-product-image-zoom, marks the dot matching the slide in view with
 * aria-current, and scrolls to a slide when its dot is tapped.
 *
 * Required markup:
 *   button[data-dot-index] — one per slide, in slide order.
 */
class SGalleryDots extends HTMLElement {
  #ac;
  #frame = 0;

  connectedCallback() {
    const root = this.closest('[data-gallery-root]') ?? this.closest('s-product-image-zoom');
    this.gallery = root?.querySelector('[data-product-gallery]');
    this.dots = [...this.querySelectorAll('[data-dot-index]')];
    if (!this.gallery || this.dots.length < 2) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal, passive: true };
    this.gallery.addEventListener('scroll', this.#onScroll, opts);
    this.addEventListener('click', this.#onClick, { signal: this.#ac.signal });
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#update);
  };

  #update = () => {
    const slides = this.gallery.children;
    const box = this.gallery.getBoundingClientRect();
    const center = box.left + box.width / 2;
    let active = 0;
    let best = Infinity;
    for (let i = 0; i < slides.length; i++) {
      const rect = slides[i].getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - center);
      if (distance < best) {
        best = distance;
        active = i;
      }
    }
    this.dots.forEach((dot, i) => {
      if (i === active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  };

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const dot = event.target.closest('[data-dot-index]');
    if (!dot) return;
    const slide = this.gallery.children[Number(dot.dataset.dotIndex)];
    if (!slide) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const offset = slide.getBoundingClientRect().left - this.gallery.getBoundingClientRect().left;
    this.gallery.scrollBy({ left: offset, behavior: reduce ? 'auto' : 'smooth' });
  };
}

if (!customElements.get('s-gallery-dots')) {
  customElements.define('s-gallery-dots', SGalleryDots);
}
